/**
 * Business Plan Scoring Service
 * Calculates quality scores for business plans (Enloop-style)
 * Pure functions - no side effects, completely safe to add
 */

import { BusinessPlan, SectionId, BusinessPlanSection } from '../types/businessPlan';
import {
  BusinessPlanScore,
  SectionScore,
  ValidationRule,
  ScoreCalculationResult,
  SECTION_SCORING_CRITERIA
} from '../types/businessPlanScoring';
import { supabase } from '../lib/supabaseClient';

/**
 * Calculate score for an individual section
 */
export function calculateSectionScore(
  sectionId: SectionId,
  section: BusinessPlanSection
): SectionScore {
  const criteria = SECTION_SCORING_CRITERIA[sectionId];
  const content = section.content || '';
  const wordCount = content.trim().split(/\s+/).length;
  
  let score = 0;
  const feedback: string[] = [];
  const missingElements: string[] = [];
  const strengths: string[] = [];
  
  // Check completion
  if (section.status !== 'completed' || !content.trim()) {
    return {
      sectionId,
      score: 0,
      status: 'incomplete',
      feedback: ['This section has not been completed yet.'],
      missingElements: criteria.requiredElements,
      strengths: [],
      wordCount: 0,
      recommendedWordCount: criteria.recommendedWordCount
    };
  }
  
  // Word count scoring (40% of section score)
  const wordCountRatio = Math.min(wordCount / criteria.recommendedWordCount, 1);
  const wordCountScore = wordCountRatio * 40;
  score += wordCountScore;
  
  if (wordCount < criteria.minWordCount) {
    feedback.push(`Content is too brief. Add at least ${criteria.minWordCount - wordCount} more words.`);
  } else if (wordCount >= criteria.recommendedWordCount) {
    strengths.push('Comprehensive content with good detail level.');
  }
  
  // Required elements check (40% of section score)
  const contentLower = content.toLowerCase();
  let elementsFound = 0;
  
  criteria.requiredElements.forEach(element => {
    // Simple keyword check (can be enhanced)
    const keywords = element.toLowerCase().split(' ');
    const found = keywords.some(keyword => contentLower.includes(keyword));
    
    if (found) {
      elementsFound++;
      strengths.push(`Includes ${element}.`);
    } else {
      missingElements.push(element);
      feedback.push(`Consider adding more detail about ${element}.`);
    }
  });
  
  const elementsScore = (elementsFound / criteria.requiredElements.length) * 40;
  score += elementsScore;
  
  // Quality indicators (20% of section score)
  let qualityScore = 0;
  criteria.qualityIndicators.forEach(indicator => {
    // Basic check - can be enhanced with AI analysis
    const keywords = indicator.toLowerCase().split(' ');
    const found = keywords.some(keyword => contentLower.includes(keyword));
    if (found) {
      qualityScore += 20 / criteria.qualityIndicators.length;
    }
  });
  score += qualityScore;
  
  // Determine status
  let status: 'excellent' | 'good' | 'needs-improvement' | 'incomplete';
  if (score >= 85) {
    status = 'excellent';
  } else if (score >= 70) {
    status = 'good';
  } else if (score >= 50) {
    status = 'needs-improvement';
  } else {
    status = 'incomplete';
  }
  
  return {
    sectionId,
    score: Math.round(score),
    status,
    feedback,
    missingElements,
    strengths,
    wordCount,
    recommendedWordCount: criteria.recommendedWordCount
  };
}

/**
 * Calculate overall quality score for a business plan
 */
export function calculateBusinessPlanScore(plan: BusinessPlan): ScoreCalculationResult {
  const sectionScores: Record<SectionId, SectionScore> = {} as Record<SectionId, SectionScore>;
  let totalWeightedScore = 0;
  let totalWeight = 0;
  let sectionsCompleted = 0;
  const allFeedback: ValidationRule[] = [];
  const allMissingElements: string[] = [];
  const allRecommendations: string[] = [];
  
  // Calculate scores for each section
  Object.entries(plan.sections).forEach(([id, section]) => {
    const sectionId = id as SectionId;
    const sectionScore = calculateSectionScore(sectionId, section);
    sectionScores[sectionId] = sectionScore;
    
    // Weight the score by section importance
    const weight = SECTION_SCORING_CRITERIA[sectionId].weight;
    totalWeightedScore += sectionScore.score * weight;
    totalWeight += weight;
    
    if (section.status === 'completed' && section.content?.trim()) {
      sectionsCompleted++;
    }
    
    // Collect feedback as validation rules
    sectionScore.feedback.forEach((message, idx) => {
      allFeedback.push({
        id: `${sectionId}-feedback-${idx}`,
        type: 'content-quality',
        description: `${section.title}: ${message}`,
        status: sectionScore.status === 'excellent' || sectionScore.status === 'good' ? 'pass' : 'warning',
        message,
        recommendation: sectionScore.missingElements.length > 0 
          ? `Add: ${sectionScore.missingElements.join(', ')}`
          : undefined
      });
    });
    
    allMissingElements.push(...sectionScore.missingElements.map(el => `${section.title}: ${el}`));
  });
  
  // Calculate overall scores
  const overallScore = Math.round(totalWeightedScore / totalWeight);
  const completenessScore = Math.round((sectionsCompleted / Object.keys(plan.sections).length) * 100);
  
  // Determine validation status
  let validationStatus: 'pass' | 'fail' | 'needs-improvement';
  if (overallScore >= 80 && completenessScore >= 90) {
    validationStatus = 'pass';
  } else if (overallScore < 50 || completenessScore < 50) {
    validationStatus = 'fail';
  } else {
    validationStatus = 'needs-improvement';
  }
  
  // Generate recommendations
  if (completenessScore < 100) {
    allRecommendations.push('Complete all sections for a comprehensive business plan.');
  }
  
  if (overallScore < 80) {
    allRecommendations.push('Add more detail to sections with lower scores.');
  }
  
  const incompleteSections = Object.values(sectionScores)
    .filter(s => s.status === 'incomplete' || s.status === 'needs-improvement')
    .map(s => s.sectionId);
  
  if (incompleteSections.length > 0) {
    allRecommendations.push(`Focus on improving: ${incompleteSections.join(', ')}`);
  }
  
  // Calculate quality score (average of section content quality)
  const contentScores = Object.values(sectionScores).map(s => s.score);
  const contentQualityScore = Math.round(
    contentScores.reduce((sum, score) => sum + score, 0) / contentScores.length
  );
  
  // Build the score object
  const score: BusinessPlanScore = {
    id: '', // Will be set by database
    business_plan_id: plan.id,
    user_id: plan.user_id || '',
    overall_score: overallScore,
    validation_status: validationStatus,
    section_scores: sectionScores,
    completeness_score: completenessScore,
    sections_completed: sectionsCompleted,
    sections_total: Object.keys(plan.sections).length,
    content_quality_score: contentQualityScore,
    detail_score: contentQualityScore, // Using content quality as detail proxy
    validation_feedback: allFeedback,
    missing_elements: allMissingElements,
    recommendations: allRecommendations,
    calculated_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  // Generate summary
  const grade = overallScore >= 90 ? 'A' : overallScore >= 80 ? 'B' : overallScore >= 70 ? 'C' : overallScore >= 60 ? 'D' : 'F';
  
  const strengths = Object.values(sectionScores)
    .filter(s => s.status === 'excellent')
    .map(s => s.sectionId);
  
  const improvements = Object.values(sectionScores)
    .filter(s => s.status === 'needs-improvement' || s.status === 'incomplete')
    .map(s => s.sectionId);
  
  const nextSteps: string[] = [];
  if (completenessScore < 100) {
    nextSteps.push('Complete all sections');
  }
  if (allMissingElements.length > 0) {
    nextSteps.push('Address missing elements in sections');
  }
  if (contentQualityScore < 80) {
    nextSteps.push('Enhance content with more detail and data');
  }
  
  return {
    score,
    summary: {
      totalScore: overallScore,
      grade,
      strengths: strengths.length > 0 ? [`Strong sections: ${strengths.join(', ')}`] : ['Keep building your plan'],
      improvements: improvements.length > 0 ? [`Focus on: ${improvements.join(', ')}`] : ['Great work!'],
      nextSteps
    }
  };
}

/**
 * Save calculated score to Supabase
 * This is optional - scores can be calculated on-the-fly without saving
 */
export async function savePlanScore(score: BusinessPlanScore): Promise<BusinessPlanScore | null> {
  try {
    const { data: userResp } = await supabase.auth.getUser();
    const userId = userResp?.user?.id;
    if (!userId) {
      return null;
    }

    const { data, error } = await supabase
      .from('business_plan_scores')
      .upsert({
        business_plan_id: score.business_plan_id,
        user_id: userId,
        overall_score: score.overall_score,
        section_scores: score.section_scores,
        completeness_score: score.completeness_score,
        sections_completed: score.sections_completed,
        sections_total: score.sections_total,
        content_quality_score: score.content_quality_score,
        detail_score: score.detail_score,
        validation_status: score.validation_status,
        validation_feedback: score.validation_feedback,
        missing_elements: score.missing_elements,
        recommendations: score.recommendations,
        calculated_at: score.calculated_at
      }, { onConflict: 'business_plan_id,user_id' })
      .select()
      .single();
    
    if (error) {
      console.error('Error saving plan score:', error);
      return null;
    }
    
    return data as BusinessPlanScore;
  } catch (error) {
    console.error('Error saving plan score:', error);
    return null;
  }
}

/**
 * Get saved score for a business plan
 */
export async function getPlanScore(planId: string): Promise<BusinessPlanScore | null> {
  try {
    const { data: userResp } = await supabase.auth.getUser();
    const userId = userResp?.user?.id;
    if (!userId) {
      return null;
    }

    const { data, error } = await supabase
      .from('business_plan_scores')
      .select('*')
      .eq('business_plan_id', planId)
      .eq('user_id', userId)
      .single();
    
    if (error) {
      console.log('No saved score found, will calculate on demand');
      return null;
    }
    
    return data as BusinessPlanScore;
  } catch (error) {
    console.error('Error fetching plan score:', error);
    return null;
  }
}
